import React, { FC, useState } from 'react'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { Form } from 'antd'
import { i18n } from 'LibNeo/utils/intl'
import { Callback } from 'Src/types/utilTypes'
import { TooltipLabelComponent } from 'LibNeo/components/Basic/Label/TooltipLabel'
import { trimPasteText, getOnChangeValue, onBlurTrim } from 'Helpers'
import { TextArea, TextAreaError, TextAreaInput } from './styled'

export type TextAreaProps = {
	disabled?: boolean
	link?: string
	linkAction?: Callback
	dataQa?: string
	errorText?: string
	form?: WrappedFormUtils
	fieldName?: string
	fieldOptions?: GetFieldDecoratorOptions
	isValidate?: boolean
	required?: boolean
	tooltip?: string
	value?: string
	clearError?: () => void
	onChange?: (string) => void
	validator?: Callback<unknown, { error?: { message?: string } }>
	label?: string
	placeholder?: string
	className?: string
	onPressEnter?: (e: React.FormEvent<EventTarget>) => void
	isTrimData?: boolean
	requiredHighlight?: boolean
}

const MAX_SYMBOLS_TO_CLEAR_INPUT = 2

export const TextAreaComponent: FC<TextAreaProps> = ({
	clearError,
	disabled = false, // гарантирует появление атрибута data-qa-disabled, ввиду типа boolean | undef
	fieldName = 'data-textarea',
	fieldOptions,
	form,
	isValidate = true,
	placeholder,
	label,
	tooltip,
	value,
	validator,
	onChange,
	link,
	linkAction,
	className,
	isTrimData,
	requiredHighlight,
	...props
}) => {
	const [hasError, setErrorState] = useState<boolean>(false)
	const [errorText, setErrorText] = useState<string>('')

	const changeValue = (v: string) => {
		const validated = validator ? validator(v, isValidate) : null

		if (validated && validated.error && validated.error.message) {
			setErrorState(true)
			setErrorText(validated.error.message)
		}

		if (v.length > MAX_SYMBOLS_TO_CLEAR_INPUT) {
			setErrorState(false)
			clearError?.()
		}

		onChange?.(v)
	}

	const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => changeValue(e.target.value)
	const inputValue = value && !form ? { value } : {}

	const propsForTrim = isTrimData
		? {
				onBlur: onBlurTrim({
					form,
					fieldName,
					onChange,
					changeValue,
					currentValue: value,
				}),
				onPaste: trimPasteText(getOnChangeValue({ form, fieldName, onChange, changeValue })),
		  }
		: {}

	const input = (
		<TextAreaInput
			data-qa-disabled={disabled}
			placeholder={i18n(placeholder)}
			{...inputValue}
			onChange={onChangeHandler}
			autoSize={{ minRows: 1 }}
			disabled={disabled}
			hasError={hasError}
			requiredHighlight={requiredHighlight}
			{...propsForTrim}
			{...props}
		/>
	)

	return (
		<TextArea>
			<TooltipLabelComponent
				title={i18n(label)}
				tooltip={i18n(tooltip)}
				link={i18n(link)}
				linkAction={linkAction}
			/>
			{form?.getFieldDecorator ? (
				<Form.Item>{form.getFieldDecorator(fieldName, fieldOptions)(input)}</Form.Item>
			) : (
				input
			)}
			{hasError && <TextAreaError>{errorText}</TextAreaError>}
		</TextArea>
	)
}
